<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { vOnClickOutside } from '@vueuse/components';
import { INPUT_TYPES } from 'dashboard/components-next/taginput/helper/tagInputHelper.js';

import TagInput from 'dashboard/components-next/taginput/TagInput.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  contacts: {
    type: Array,
    required: true,
  },
  selectedContact: {
    type: Object,
    default: null,
  },
  showContactsDropdown: {
    type: Boolean,
    required: true,
  },
  isLoading: {
    type: Boolean,
    required: true,
  },
  isCreatingContact: {
    type: Boolean,
    required: true,
  },
  contactId: {
    type: String,
    default: null,
  },
  contactableInboxesList: {
    type: Array,
    default: () => [],
  },
  showInboxesDropdown: {
    type: Boolean,
    required: true,
  },
  hasErrors: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'searchContacts',
  'setSelectedContact',
  'clearSelectedContact',
  'updateDropdown',
]);

const i18nPrefix = 'COMPOSE_NEW_CONVERSATION.FORM.CONTACT_SELECTOR';
const { t } = useI18n();

const inputType = ref(INPUT_TYPES.TEL);
const selectedCountryCode = ref('+55');
const currentInput = ref('');
const isCurrentInputCpf = ref(false);
const showCountryCodeDropdown = ref(false);

const countryCodeOptions = [
  { value: '+55', flag: '🇧🇷', country: 'Brasil' },
  { value: '+1', flag: '🇺🇸', country: 'EUA/Canada' },
  { value: '+351', flag: '🇵🇹', country: 'Portugal' },
  { value: '+54', flag: '🇦🇷', country: 'Argentina' },
  { value: '+56', flag: '🇨🇱', country: 'Chile' },
  { value: '+57', flag: '🇨🇴', country: 'Colombia' },
  { value: '+52', flag: '🇲🇽', country: 'Mexico' },
];

const selectedCountryCodeOption = computed(() =>
  countryCodeOptions.find(option => option.value === selectedCountryCode.value)
);

const contactsList = computed(() => {
  return props.contacts?.map(({ name, id, thumbnail, email, ...rest }) => ({
    id,
    label: email ? `${name} (${email})` : name,
    value: id,
    thumbnail: { name, src: thumbnail },
    ...rest,
    name,
    email,
    action: 'contact',
  }));
});

const selectedContactLabel = computed(() => {
  const { name, email = '', phoneNumber = '' } = props.selectedContact || {};
  if (email) {
    return `${name} (${email})`;
  }
  if (phoneNumber) {
    return `${name} (${phoneNumber})`;
  }
  return name || '';
});

const errorClass = computed(() => {
  return props.hasErrors
    ? '[&_input]:placeholder:!text-n-ruby-9 [&_input]:dark:placeholder:!text-n-ruby-9'
    : '';
});

const isCpfValue = value => {
  const trimmedValue = value?.trim() || '';
  return /^\d{3}\.\d{3}\.\d{3}-\d{2}$/.test(trimmedValue);
};

const canCreateContactFromInput = computed(() => !isCurrentInputCpf.value);

const isPhoneValue = value => {
  const trimmedValue = value?.trim() || '';
  if (!trimmedValue) return true;
  if (isCpfValue(trimmedValue)) return false;
  return /^\+?[\d\s().-]+$/.test(trimmedValue);
};

const isNumericSearchValue = value => {
  const trimmedValue = value?.trim() || '';
  return /^\+?[\d\s().-]+$/.test(trimmedValue);
};

const getNationalPhoneDigits = value => {
  const trimmedValue = value?.trim() || '';
  if (!trimmedValue) return '';

  const digits = trimmedValue.replace(/\D/g, '');
  const countryDigits = selectedCountryCode.value.replace(/\D/g, '');
  const includesSelectedCountryCode =
    digits.startsWith(countryDigits) &&
    (trimmedValue.startsWith('+') || digits.length > 11);

  return includesSelectedCountryCode
    ? digits.slice(countryDigits.length)
    : digits;
};

const normalizePhoneValue = value => {
  const trimmedValue = value?.trim() || '';
  if (!trimmedValue || !isPhoneValue(trimmedValue)) return trimmedValue;

  const nationalDigits = getNationalPhoneDigits(trimmedValue);
  return nationalDigits ? `${selectedCountryCode.value}${nationalDigits}` : '';
};

const normalizeInputValue = value => {
  return isPhoneValue(value) ? normalizePhoneValue(value) : value?.trim() || '';
};

const normalizeSearchValue = value => {
  return isNumericSearchValue(value)
    ? getNationalPhoneDigits(value)
    : value?.trim() || '';
};

const handleInput = value => {
  isCurrentInputCpf.value = isCpfValue(value);

  const normalizedInput = isNumericSearchValue(value)
    ? getNationalPhoneDigits(value)
    : value?.trim() || '';

  currentInput.value = normalizedInput;
  if (isCurrentInputCpf.value) {
    inputType.value = INPUT_TYPES.TEXT;
  } else {
    inputType.value = isPhoneValue(normalizedInput)
      ? INPUT_TYPES.TEL
      : INPUT_TYPES.EMAIL;
  }
  emit('searchContacts', normalizeSearchValue(normalizedInput));
};

const handleCountryCodeChange = () => {
  if (!currentInput.value || !isPhoneValue(currentInput.value)) return;
  emit('searchContacts', normalizeSearchValue(currentInput.value));
};

const selectCountryCode = value => {
  selectedCountryCode.value = value;
  showCountryCodeDropdown.value = false;
  handleCountryCodeChange();
};

const handleAdd = ({ value, action, ...rest }) => {
  const selectedValue =
    action === 'create' ? normalizeInputValue(value) : value;
  emit('setSelectedContact', { value: selectedValue, action, ...rest });
};
</script>

<template>
  <div class="relative flex-1 overflow-y-visible bg-n-solid-1 px-5 py-4">
    <div
      class="grid w-full gap-2.5 sm:grid-cols-[6.5rem_minmax(0,1fr)] sm:items-center"
    >
      <label
        class="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-n-slate-10"
      >
        <span class="compose-field-icon">
          <span class="i-lucide-user-round-search size-3.5" />
        </span>
        {{ t(`${i18nPrefix}.LABEL`) }}
      </label>

      <div
        v-if="isCreatingContact"
        class="flex items-center gap-1.5 rounded-md bg-n-alpha-2 px-3 min-h-7 min-w-0"
      >
        <span class="text-sm truncate text-n-slate-12">
          {{ t(`${i18nPrefix}.CONTACT_CREATING`) }}
        </span>
      </div>
      <div
        v-else-if="selectedContact"
        class="flex items-center gap-1.5 rounded-md bg-n-alpha-2 min-h-7 min-w-0"
        :class="!contactId ? 'ltr:pl-3 rtl:pr-3 ltr:pr-1 rtl:pl-1' : 'px-3'"
      >
        <span class="text-sm truncate text-n-slate-12">
          {{
            isCreatingContact
              ? t(`${i18nPrefix}.CONTACT_CREATING`)
              : selectedContactLabel
          }}
        </span>
        <Button
          v-if="!contactId"
          variant="ghost"
          icon="i-lucide-x"
          color="slate"
          :disabled="contactId"
          size="xs"
          @click="emit('clearSelectedContact')"
        />
      </div>
      <div v-else class="grid min-w-0 gap-2 sm:grid-cols-[7.5rem_1fr]">
        <div
          v-on-click-outside="() => (showCountryCodeDropdown = false)"
          class="relative"
        >
          <button
            type="button"
            class="flex h-10 w-full items-center gap-2 rounded-xl border border-n-weak bg-n-solid-2 px-3 text-sm font-medium text-n-slate-12 shadow-sm outline-none transition hover:border-n-brand/50 hover:bg-n-alpha-2 focus:border-n-brand focus:ring-2 focus:ring-n-brand/15"
            :disabled="
              contactableInboxesList?.length > 0 && showInboxesDropdown
            "
            @click.stop="showCountryCodeDropdown = !showCountryCodeDropdown"
          >
            <span class="text-base leading-none">
              {{ selectedCountryCodeOption.flag }}
            </span>
            <span class="tabular-nums">
              {{ selectedCountryCodeOption.value }}
            </span>
            <span
              class="i-lucide-chevron-down ml-auto size-3.5 text-n-slate-10"
            />
          </button>
          <div
            v-if="showCountryCodeDropdown"
            class="absolute left-0 top-9 z-[110] w-52 overflow-hidden rounded-xl border border-n-weak bg-n-solid-1 p-1 shadow-lg"
          >
            <button
              v-for="option in countryCodeOptions"
              :key="option.value"
              type="button"
              class="flex h-9 w-full items-center gap-2 rounded-lg px-2 text-left text-sm text-n-slate-12 hover:bg-n-alpha-2"
              @click.stop="selectCountryCode(option.value)"
            >
              <span class="text-base leading-none">{{ option.flag }}</span>
              <span class="w-12 tabular-nums">{{ option.value }}</span>
              <span class="truncate text-n-slate-11">{{ option.country }}</span>
            </button>
          </div>
        </div>
        <TagInput
          :placeholder="t(`${i18nPrefix}.TAG_INPUT_PLACEHOLDER`)"
          mode="single"
          :menu-items="contactsList"
          :show-dropdown="showContactsDropdown"
          :is-loading="isLoading"
          :disabled="contactableInboxesList?.length > 0 && showInboxesDropdown"
          :allow-create="canCreateContactFromInput"
          :type="inputType"
          :value-transformer="normalizeInputValue"
          class="min-h-10 min-w-0 [&_input]:!h-10 [&_input]:!rounded-xl [&_input]:!bg-n-solid-2 [&_input]:!px-4"
          :class="errorClass"
          focus-on-mount
          @input="handleInput"
          @on-click-outside="emit('updateDropdown', 'contacts', false)"
          @add="handleAdd"
          @remove="emit('clearSelectedContact')"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
.compose-field-icon {
  @apply grid size-6 place-items-center rounded-lg bg-n-brand/10 text-n-brand;
}
</style>
